import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { isEmailIdentifier, toSsoLoginError, type ISsoDiscovery } from '@d-orca/shared/client/utils';
import { useAuth } from '@contexts/useAuth';
import { InvalidCredentialsError, SsoRequiredError, forgotPassword } from '@apis/auth';
import { discoverSso, startSsoLogin } from '@apis/sso';
import type { IMfaSession } from '@apis/mfa';
import { MfaChallenge } from '@components/Mfa/MfaChallenge/MfaChallenge';
import { MfaEnrollment } from '@components/Mfa/MfaEnrollment/MfaEnrollment';
import type { MfaChallengeMethod, MfaOtpChannel } from '@interfaces/IMfa';
import { SsoContinueStep } from './SsoContinueStep';
import styles from './styles.module.css';

/**
 * The sign-in card is a single-form surface that grows a second step when the
 * account is MFA-protected:
 *  - `login` / `forgot` — email-first password + reset flows;
 *  - `sso`    — the address signs in with its company account (enterprise SSO,
 *    spec 2026-10-15): the page navigates away and comes back signed in, or to
 *    `/login?error=…`;
 *  - `mfa`    — `POST /api/auth/login` answered `{mfaRequired, methods}`;
 *  - `enroll` — it answered `{enrollmentRequired}` (role forced into MFA but
 *    never enrolled), so enrollment happens before a session is ever issued.
 *
 * The MFA stages are steps of the same card rather than their own route: the
 * whole flow is authorised by an httpOnly `dorca_pending` cookie with no
 * client-visible state, so a dedicated URL would be a dead end on reload.
 */
type Stage =
  /**
   * Email first: `identify` asks only for the address and finds out how it
   * signs in; `password` is today's form. A username (no `@`) skips straight to
   * `password` — only password users have one.
   */
  | { kind: 'login'; step: 'identify' | 'password' }
  /** The address signs in with its company account: no password field at all. */
  | { kind: 'sso'; displayName?: string; logoUrl?: string }
  | { kind: 'forgot' }
  | {
      kind: 'mfa';
      methods: MfaChallengeMethod[];
      otpChannels?: MfaOtpChannel[];
      primaryOtpChannel?: MfaOtpChannel;
    }
  | { kind: 'enroll' };

/** The login form's one button: "Continue" while identifying, "Sign in" once a password is asked for. */
function submitLabel(t: TFunction, step: 'identify' | 'password', submitting: boolean): string {
  if (step === 'identify') return submitting ? t('auth.login.checkingAccount') : t('auth.login.continue');
  return submitting ? t('auth.login.submitting') : t('auth.login.submit');
}

export function LoginPage() {
  const { t } = useTranslation();
  const { login, completeMfaLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<Stage>({ kind: 'login', step: 'identify' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A company sign-in that came back with `?error=` lands here with its reason.
  const [err, setErr] = useState<string | null>(() => {
    const ssoError = toSsoLoginError(new URLSearchParams(location.search).get('error'));
    return ssoError === null ? null : t(`auth.login.ssoError.${ssoError}`);
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';
  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  /**
   * The password step belongs to the address it was asked for. Editing the
   * address goes back to step one, so a new address is asked how it signs in —
   * an SSO address must never be shown a password field it has no password for.
   */
  function onEmailChange(value: string) {
    setEmail(value);
    if (stage.kind === 'login' && stage.step === 'password') {
      setPassword('');
      setErr(null);
      setStage({ kind: 'login', step: 'identify' });
    }
  }

  /** Step one: an email asks how it signs in; anything else is a username, so a password follows. */
  async function onIdentify(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setNotice(null);
    if (!isEmailIdentifier(email)) {
      setStage({ kind: 'login', step: 'password' });
      return;
    }
    setSubmitting(true);
    const discovery = await discoverSso(email);
    setSubmitting(false);
    setStage(
      discovery.sso
        ? { kind: 'sso', displayName: discovery.displayName, logoUrl: discovery.logoUrl }
        : { kind: 'login', step: 'password' },
    );
  }

  /**
   * The password was refused because the address must use its company sign-in
   * (an email typed past the identify step, or a username — whose email we do
   * not know, so the user is asked for it).
   */
  async function onSsoRequired() {
    setPassword('');
    if (!isEmailIdentifier(email)) {
      setEmail('');
      setNotice(t('auth.login.ssoRequiredEnterEmail'));
      setStage({ kind: 'login', step: 'identify' });
      return;
    }
    const discovery = await discoverSso(email);
    setNotice(t('auth.login.ssoRequired'));
    setStage({ kind: 'sso', displayName: discovery.displayName, logoUrl: discovery.logoUrl });
  }

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const outcome = await login(email, password);
      if (outcome.kind === 'mfa-required') {
        setStage({
          kind: 'mfa',
          methods: outcome.methods,
          otpChannels: outcome.otpChannels,
          primaryOtpChannel: outcome.primaryOtpChannel,
        });
      } else if (outcome.kind === 'enrollment-required') {
        setStage({ kind: 'enroll' });
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      if (error instanceof SsoRequiredError) {
        await onSsoRequired();
        return;
      }
      // Neutral error — never distinguish "no such user" from "wrong password".
      setErr(
        error instanceof InvalidCredentialsError
          ? t('auth.login.invalidCredentials')
          : t('auth.login.genericError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function onForgot(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    // A company-account address has no password, and Trusstor sends it nothing —
    // so say so and offer the company sign-in instead of a confirmation that a
    // link is on its way. Discovery answers per DOMAIN and is public (the
    // identify step asks the same), so this reveals nothing about the account.
    const discovery: ISsoDiscovery = isEmailIdentifier(email) ? await discoverSso(email) : { sso: false };
    if (discovery.sso) {
      setSubmitting(false);
      setNotice(
        discovery.displayName
          ? t('auth.forgot.ssoNoPassword', { name: discovery.displayName })
          : t('auth.forgot.ssoNoPasswordGeneric'),
      );
      setStage({ kind: 'sso', displayName: discovery.displayName, logoUrl: discovery.logoUrl });
      return;
    }
    // The endpoint always 200s and never reveals whether the account exists,
    // so we show the same confirmation regardless of outcome.
    await forgotPassword(email);
    setSubmitting(false);
    setForgotSent(true);
  }

  function switchMode(next: 'login' | 'forgot') {
    setErr(null);
    setNotice(null);
    setForgotSent(false);
    setStage(next === 'login' ? { kind: 'login', step: 'identify' } : { kind: 'forgot' });
  }

  /** The second factor landed — adopt the promoted session and go where we were headed. */
  async function onAuthenticated(session: IMfaSession) {
    await completeMfaLogin(session.user, session.projects);
    navigate(redirectTo, { replace: true });
  }

  /**
   * `dorca_pending` aged out mid-challenge. Drop the password the user already
   * typed and send them back to the start with an explanation — never surface
   * expiry as a code failure (that would confirm the code was otherwise right).
   */
  function onPendingExpired() {
    setPassword('');
    setErr(null);
    setForgotSent(false);
    setNotice(t('auth.mfa.pendingExpired'));
    setStage({ kind: 'login', step: 'password' });
  }

  /** User abandoned the MFA step (wrong account, no device to hand). */
  function abandonMfa() {
    setPassword('');
    setErr(null);
    setNotice(null);
    setStage({ kind: 'login', step: 'password' });
  }

  /** Back from the company-sign-in step to type a different address. */
  function switchAccount() {
    setErr(null);
    setNotice(null);
    setStage({ kind: 'login', step: 'identify' });
  }

  const wide = stage.kind === 'enroll';

  return (
    <div className={styles.page}>
      <div className={styles.grid} aria-hidden />
      <main className={wide ? `${styles.card} ${styles.cardWide}` : styles.card}>
        <div className={styles.accent} aria-hidden />
        {/* The SSO step opens with its own d-orca → company hand-off instead. */}
        {stage.kind === 'sso' ? null : (
          <header className={styles.brand}>
            <img className={styles.logo} src="/orca.png" alt="" />
            <span className={styles.wordmark}>d-orca</span>
          </header>
        )}

        {stage.kind === 'mfa' ? (
          <MfaChallenge
            methods={stage.methods}
            otpChannels={stage.otpChannels}
            primaryOtpChannel={stage.primaryOtpChannel}
            onVerified={onAuthenticated}
            onPendingExpired={onPendingExpired}
            onCancel={abandonMfa}
          />
        ) : stage.kind === 'enroll' ? (
          <MfaEnrollment
            context="pending"
            onConfirmed={async (result) => {
              // From a pending state the contract promises `{user}`; if the
              // backend ever answers `{enabled:true}` here there is no session
              // to adopt, so bounce to the password form rather than hang.
              if (result.kind === 'authenticated') await onAuthenticated(result.session);
              else onPendingExpired();
            }}
            onPendingExpired={onPendingExpired}
            onCancel={abandonMfa}
          />
        ) : stage.kind === 'sso' ? (
          <SsoContinueStep
            email={email.trim()}
            displayName={stage.displayName}
            logoUrl={stage.logoUrl}
            notice={notice}
            onContinue={() => startSsoLogin(email, redirectTo)}
            onUseDifferentAccount={switchAccount}
          />
        ) : stage.kind === 'login' ? (
          <>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{t('auth.login.eyebrow')}</span>
              <h1 className={styles.title}>{t('auth.login.title')}</h1>
              <p className={styles.subtitle}>{t('auth.login.subtitle')}</p>
            </div>
            {notice ? (
              <div className={styles.notice} role="status">
                {notice}
              </div>
            ) : null}
            <form onSubmit={stage.step === 'identify' ? onIdentify : onLogin} className={styles.form} noValidate>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('auth.login.emailLabel')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  autoComplete="username"
                  autoFocus={stage.step === 'identify'}
                  required
                />
              </label>
              {stage.step === 'password' ? (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('auth.login.passwordLabel')}</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    autoFocus
                    required
                  />
                </label>
              ) : null}
              {err ? <div className={styles.error} role="alert">{err}</div> : null}
              <button type="submit" className={styles.submit} disabled={submitting || email.trim() === ''}>
                <span>{submitLabel(t, stage.step, submitting)}</span>
                <svg className={styles.arrow} viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </form>
            <div className={styles.forgotRow}>
              <button type="button" className={styles.linkButton} onClick={() => switchMode('forgot')}>
                {t('auth.login.forgotPassword')}
              </button>
            </div>
            <footer className={styles.footer}>
              <span>{t('auth.login.footerHelp')}</span>
              <span className={styles.footerSep} aria-hidden>·</span>
              <span>{t('auth.login.footerContact')}</span>
            </footer>
          </>
        ) : (
          <>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{t('auth.login.eyebrow')}</span>
              <h1 className={styles.title}>{t('auth.forgot.title')}</h1>
              <p className={styles.subtitle}>{t('auth.forgot.subtitle')}</p>
            </div>
            {forgotSent ? (
              <div className={styles.confirmation} role="status">
                {t('auth.forgot.confirmation')}
              </div>
            ) : (
              <form onSubmit={onForgot} className={styles.form} noValidate>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('auth.forgot.emailLabel')}</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </label>
                <button type="submit" className={styles.submit} disabled={submitting}>
                  <span>{submitting ? t('auth.forgot.submitting') : t('auth.forgot.submit')}</span>
                </button>
              </form>
            )}
            <div className={styles.forgotRow}>
              <button type="button" className={styles.linkButton} onClick={() => switchMode('login')}>
                {t('auth.forgot.back')}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
